import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import {
  EMPTY_PORTFOLIO_KPIS,
  EXPANDED_BAR_CHART_OPTION,
  INIT_CHART_OPTION_BIG,
  SORT_ORDER
} from '@catalogues';
import { ExpandedViewComponent } from '@components';
import {
  Kpi,
  KpiCategories,
  KpiKeys,
  KpisByKey,
  KpiTimeCycleChartData,
  Message,
  PortfolioMilestone,
  Segment,
  SortOrder,
  Study,
  TimeCycleChart
} from '@models';
import {
  AppHeaderService,
  CommonFunctionsService,
  FilterClientState,
  FilterState,
  PortfolioMilestoneFilterService,
  SelectedKpiService,
  SidebarService,
  StudyFilterService,
  StudyInsightsService,
  StudyService
} from '@services';
import {
  columnConfigurations,
  getMilestoneTooltipDeltaParts as getMilestoneTooltipDeltaPartsUtil,
  getMilestoneTooltipTitle as getMilestoneTooltipTitleUtil,
  parseToLineChartData,
  shouldShowMilestoneTooltip as shouldShowMilestoneTooltipUtil,
  sortByCustomOrder,
  sortStandard
} from '@utils';
import { KpiCommonFunctionsService } from 'app/services/kpi-common-functions/kpi-common-functions.service';
import {
  auditTime,
  catchError,
  debounceTime,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap
} from 'rxjs';
const PAGINATION_SIZE = 10;
type EmailProvider = 'gmail' | 'outlook' | 'default';
interface MilestoneStatusSummaryItem {
  label: string;
  value: number;
}

interface MailtoPayload {
  to: string;
  subject: string;
  body: string;
}

@Component({
  selector: 'study-portfolio',
  templateUrl: './study-portfolio.component.html',
  styleUrl: './study-portfolio.component.scss'
})
export class StudyPortfolioComponent implements OnInit, OnDestroy {
  private static readonly HIDE_TOOLTIP_DELAY_MS = 150;

  private readonly milestoneStatusOrder: string[] = [
    'Upcoming',
    'On Track',
    'At Risk',
    'Past Due',
    'Completed',
    'Completed | Accelerated',
    'Completed | On Time',
    'Completed | Late'
  ];
  private readonly milestoneStatusLookup = new Map<string, string>(
    this.milestoneStatusOrder.map((status) => [
      status
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\s*\|\s*/g, ' | ')
        .toLowerCase(),
      status
    ])
  );

  private destroy$ = new Subject<void>();
  private kpiRequest$ = new Subject<void>();
  private kpiPanelRequest$ = new Subject<void>();
  private kpiPanelCycleRequest$ = new Subject<void>();
  private studyService: StudyService = inject(StudyService);
  private studyFilterService: StudyFilterService = inject(StudyFilterService);
  private milestoneFilterService: PortfolioMilestoneFilterService = inject(
    PortfolioMilestoneFilterService
  );
  private sidebarService: SidebarService = inject(SidebarService);
  private appHeaderService: AppHeaderService = inject(AppHeaderService);
  private aiService: StudyInsightsService = inject(StudyInsightsService);
  private selectedKpiService: SelectedKpiService = inject(SelectedKpiService);
  private kpiCommonFunctionsService: KpiCommonFunctionsService = inject(KpiCommonFunctionsService);
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private parentClientNames: string[] = [];
  pageTitle = 'My portfolio';
  expandedChartTitle = 'Studies by therapeutic area';
  hasData = true;
  isLoading = true;
  isKpiLoading = true;
  isKpiPanelKpiLoading = false;
  isKpiPanelCycleLoading = false;
  isMilestoneSummaryLoading = true;
  hasError = false;
  columnConfigurations = columnConfigurations;
  allColumns: (keyof Study)[] = Object.keys(columnConfigurations) as (keyof Study)[];
  displayedColumns: (keyof Study)[] = [];
  sponsorNames = this.studyService.getClientNamesList();
  moreThanOneSponsor: boolean = this.sponsorNames.length > 1;
  sponsorSelected = sessionStorage.getItem('sponsorSelected') || 'All';
  portfolioAiSummary: Observable<string | Segment[]> = of('Loading...');

  private updateDisplayedColumns(): void {
    const clientNamesList = this.studyService.getClientNamesList();
    if (clientNamesList.length > 1) {
      this.displayedColumns = [...this.allColumns];
    } else {
      this.displayedColumns = this.allColumns.filter((_, index) => index !== 0 && index !== 2);
    }
  }

  columnTitleMapping: Record<string, string> = {
    ...Object.fromEntries(
      Object.entries(columnConfigurations).map(([key, config]) => [key, config.displayName])
    ),
    status: 'Status',
    milestoneName: 'Milestone name',
    parentClientName: 'Sponsor/Client'
  };

  sourceData!: Study[];
  currentStudiesDisplayed = 0;
  displayedData: Study[] = [];
  tableData = new MatTableDataSource<Study>([]);
  sidebarCollapsed!: boolean;
  sortedStudiesByTherapeuticArea: any = {};
  sortedStudiesByPhase: any = {};
  sortedStudiesByStage: any = {};
  sortedStudiesByAccelerator: any = {};
  currentSort: Sort = { active: 'protocolNumber', direction: 'asc' };
  totalFiltersApplied = 0;
  currentPage = 1;
  resultsPerPage = [10];
  activeResultsPerPage = 10;
  totalResults = 50;
  showFilterMenu = false;
  showHeaderMenu = false;
  filterMenuPosition = { top: 55, left: 85 };
  headerMenuPosition = { top: 55, left: 85 };
  paginationVariants = JSON.stringify({
    dropdown: true,
    indicator: true,
    goto: false
  });
  currentSortColumn: string | null = null;
  currentSortDirection: 'asc' | 'desc' | null = null;
  activeMenuColumn: string | null = null;
  kpiCardTitle: KpiCategories = 'Portfolio KPIs';
  kpis: KpisByKey = {};
  dialog = inject(MatDialog);
  chartData$: any;
  showModal = false;
  trendText = 'since 30 days ago';
  showProviderModal = false;
  emailPayload: MailtoPayload | null = null;
  placeholderMilestoneRows: MatTableDataSource<any> = new MatTableDataSource<any>();
  milestoneStatusSummaryData: MilestoneStatusSummaryItem[] = [
    { label: 'Upcoming', value: 12 },
    { label: 'On Track', value: 10 },
    { label: 'At Risk', value: 9 },
    { label: 'Past Due', value: 8 },
    { label: 'Completed', value: 15 },
    { label: 'Completed | Accelerated', value: 8 },
    { label: 'Completed | On Time', value: 6 },
    { label: 'Completed | Late', value: 8 }
  ];
  allMilestoneStatusSummaryData: MilestoneStatusSummaryItem[] = [
    ...this.milestoneStatusSummaryData
  ];
  portfolioTimeCycleChartData: KpiTimeCycleChartData = this.getEmptyPortfolioTimeCycleChartData();
  private portfolioMilestones: PortfolioMilestone[] = [];
  private kpiPanelFilteredStudyCount = 0;
  private kpiPanelFilteredKpi?: Kpi;
  private kpiPanelFiltersAppliedState: Record<string, string[]> = {};
  private totalKpiPanelFiltersAppliedState = 0;
  readonly portfolioMilestonesKpiKey: KpiKeys = 'key_milestones_on_track_to_baseline';
  portfolioSidePanelExpanded = false;
  portfolioExpandedPanelExpanded = false;
  selectedPortfolioKpi?: { key: KpiKeys; kpi: Kpi };
  showPaginator = true;
  @ViewChild('paginator', { read: ElementRef }) paginatorRef!: ElementRef;
  countryTooltipElement: Study | null = null;
  ppdRegionTooltipElement: Study | null = null;
  protocolTooltipOpen = false;
  protocolTooltipElement: Study | null = null;
  milestoneTooltipOpen = false;
  milestoneTooltipElement: Study | null = null;
  readonly shouldShowMilestoneTooltip = shouldShowMilestoneTooltipUtil;
  readonly getMilestoneTooltipTitle = getMilestoneTooltipTitleUtil;
  readonly getMilestoneTooltipDeltaParts = getMilestoneTooltipDeltaPartsUtil;
  private protocolTooltipHideTimeoutId: number | null = null;
  private isProtocolAnchorHovered = false;
  private isProtocolTooltipHovered = false;
  private milestoneTooltipHideTimeoutId: number | null = null;
  private isMilestoneAnchorHovered = false;
  private isMilestoneTooltipHovered = false;

  getElementValue(element: Study, column: string): any {
    return element[column as keyof Study] ?? '';
  }

  openHeaderMenu() {
    this.headerMenuPosition = { top: 55, left: 260 };
    this.showHeaderMenu = true;
  }

  closeMenu() {
    this.activeMenuColumn = null;
    this.showHeaderMenu = false;
  }

  toggleCountryTooltip(element: Study): void {
    this.countryTooltipElement = this.countryTooltipElement === element ? null : element;
  }

  onCountryTooltipChange(isOpen: boolean, element: Study): void {
    this.countryTooltipElement = isOpen ? element : null;
  }

  togglePpdRegionTooltip(element: Study): void {
    this.ppdRegionTooltipElement = this.ppdRegionTooltipElement === element ? null : element;
  }

  onPpdRegionTooltipChange(isOpen: boolean, element: Study): void {
    this.ppdRegionTooltipElement = isOpen ? element : null;
  }

  onDropdownApply(pending: string): void {
    // Apply the sponsor filter directly
    this.sponsorSelected = pending;
    sessionStorage.setItem('sponsorSelected', pending);

    this.studyFilterService.removeClientFilter();
    if (pending !== 'All') {
      this.studyFilterService.addClientFilter(true, pending);
    }
  }

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    if (ms) {
      // Initialize the sort from current values if they exist
      if (this.currentSort.active && this.currentSort.direction) {
        ms.active = this.currentSort.active;
        ms.direction = this.currentSort.direction;
        this.currentSortColumn = this.currentSort.active as string;
        this.currentSortDirection = this.currentSort.direction;
      }

      ms.sortChange.subscribe((sort: Sort) => {
        this.currentSort = sort;
        this.currentSortColumn = sort.active as string;
        this.currentSortDirection = sort.direction as 'asc' | 'desc' | null;
        this.applySortAndPagination();
      });
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth < 1400) {
      this.sidebarService.collapseSidebarDOM();
    }
  }

  async ngOnInit() {
    // Listen for sponsor apply events from header
    this.appHeaderService
      .getSponsorApplied()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sponsor) => {
        this.onDropdownApply(sponsor);
      });

    this.updateDisplayedColumns();
    this.selectedKpiService.selectedKpi$.pipe(takeUntil(this.destroy$)).subscribe((kpiKey) => {
      this.handlePortfolioKpiSelection(kpiKey);
    });

    this.parentClientNames = this.studyService.getParentClientNames();
    this.subscribeToMilestonePanelFilterState();
    this.loadMilestoneStatusSummaryOnce();

    // Initialize sort tracking properties
    if (this.currentSort.active && this.currentSort.direction) {
      this.currentSortColumn = this.currentSort.active as string;
      this.currentSortDirection = this.currentSort.direction;
    }

    let previousFilterCount = 0;
    let previousClientFilter: FilterClientState = { active: false, client: '' };
    let initialKpiLoaded = false;

    this.studyFilterService.filterState$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((filterState: FilterState) => {
        this.displayedData = filterState.filteredData;
        this.tableData.data = filterState.filteredData;
        this.totalResults = filterState.filteredData.length;
        this.totalFiltersApplied = filterState.totalFilterCount;
        this.portfolioAiSummary = this.aiService
          .getPortfolioSummary(filterState.filteredData, filterState.appliedFilters)
          .pipe(map((insights) => this.mapResponseState(insights)));
        this.resetPaginatorUI();

        this.hasData = filterState.filteredData.length > 0;

        if (filterState.filteredData.length < PAGINATION_SIZE) {
          this.resultsPerPage = [filterState.filteredData.length];
          this.activeResultsPerPage = filterState.filteredData.length;
        } else {
          this.resultsPerPage = [PAGINATION_SIZE];
          this.activeResultsPerPage = PAGINATION_SIZE;
        }

        this.applySortAndPagination();
        this.updateChartData(filterState.filteredData);

        if (!initialKpiLoaded) {
          this.updateKpiInfo([], true);
          initialKpiLoaded = true;
        } else if (previousFilterCount !== filterState.totalFilterCount) {
          this.updateKpiInfo(filterState.filteredData);
        }

        previousFilterCount = filterState.totalFilterCount;
      });

    this.studyFilterService.filterClientState$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((filterClientState: FilterClientState) => {
        const clientFilterChanged =
          previousClientFilter.active !== filterClientState.active ||
          previousClientFilter.client !== filterClientState.client;

        if (filterClientState.active) {
          // When client filter is active, count studies for that specific client from source data
          const clientStudies =
            this.sourceData?.filter((study) => study.clientName === filterClientState.client) || [];
          this.currentStudiesDisplayed = clientStudies.length;
        } else {
          // When client filter is inactive, show total source data count
          this.currentStudiesDisplayed = this.sourceData?.length || 0;
        }

        if (clientFilterChanged && initialKpiLoaded) {
          // When client filter changed and initial kpi is loaded, update KPIs
          const currentState = this.studyFilterService.getCurrentState();
          this.updateKpiInfo(currentState.filteredData);
        }

        previousClientFilter = { ...filterClientState };
      });

    this.loadStudiesData();

    this.sidebarService.sidebarCollapsed$.subscribe((collapsed: boolean) => {
      this.sidebarCollapsed = collapsed;
    });
  }

  private loadStudiesData(): void {
    this.studyService
      .getStudiesPortfolio(this.parentClientNames)
      .pipe(
        tap((response: Study[]) => {
          this.sourceData = structuredClone(response);
          this.displayedData = structuredClone(this.sourceData);
          this.currentStudiesDisplayed = this.displayedData.length;
          this.portfolioAiSummary = this.aiService
            .getPortfolioSummary(this.sourceData)
            .pipe(map((insights) => this.mapResponseState(insights)));

          this.sourceData.forEach((study: Study) => {
            if (study.country != null) {
              const countriesList = study.country.split(',').map((country) => country.trim());
              study.countryList = countriesList;
              study.country = countriesList.length > 1 ? 'Multiple' : countriesList[0];
            }

            if (study.ppdRegion == null && study.region != null) {
              study.ppdRegion = study.region;
            }

            if (study.ppdRegion != null) {
              const ppdRegionsList = study.ppdRegion.split(',').map((region) => region.trim());
              study.ppdRegionList = ppdRegionsList;
              study.ppdRegion = ppdRegionsList.length > 1 ? 'Multiple' : ppdRegionsList[0];
            }
          });

          this.studyFilterService.initializeFilters(this.sourceData);

          // Check sessionStorage and apply client filter after data is loaded
          this.applySessionStorageSponsorSelection();

          this.applySortAndPagination();
          this.isLoading = false;
        }),
        catchError((err: any) => {
          console.error('Error fetching studies portfolio', err);
          this.isLoading = false;
          this.hasData = false;
          this.hasError = true;
          return of(null);
        })
      )
      .subscribe();
  }

  private applySessionStorageSponsorSelection(): void {
    const savedSponsor = sessionStorage.getItem('sponsorSelected');
    if (savedSponsor && savedSponsor !== 'All') {
      this.sponsorSelected = savedSponsor;
      // Apply the client filter after data is loaded
      this.studyFilterService.addClientFilter(true, savedSponsor);
    } else {
      this.sponsorSelected = 'All';
      // Ensure client filter is cleared
      this.studyFilterService.removeClientFilter();
    }
  }

  ngOnDestroy() {
    this.milestoneFilterService.restoreOriginalStudyFilters();
    this.selectedKpiService.setSelectedKpi(undefined);
    this.destroy$.next();
    this.destroy$.complete();
    this.kpiRequest$.complete();
    this.kpiPanelRequest$.complete();
    this.kpiPanelCycleRequest$.complete();
    if (this.protocolTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
    }
    if (this.milestoneTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.milestoneTooltipHideTimeoutId);
    }
  }

  updateChartData(data: Study[]): void {
    const chartData = parseToLineChartData(
      'therapeuticArea',
      data,
      ({ therapeuticArea }: Study) => therapeuticArea
    );

    if (chartData['NULL']) {
      delete chartData['NULL'];
    }

    this.sortedStudiesByTherapeuticArea = chartData;

    this.sortedStudiesByPhase = parseToLineChartData(
      'studyPhase',
      data,
      ({ studyPhaseSimpleLabel }: Study) => studyPhaseSimpleLabel
    );

    this.sortedStudiesByStage = parseToLineChartData(
      'studyStage',
      data,
      ({ studyStage }: Study) => studyStage
    );

    const studiesByAccelerator = Object.groupBy(data, ({ accelerator }) => accelerator);
    const acceleratorEntries = Object.entries(studiesByAccelerator);
    acceleratorEntries.sort(([, arrA], [, arrB]) => (arrA?.length || 0) - (arrB?.length || 0));
    this.sortedStudiesByAccelerator = Object.fromEntries(acceleratorEntries);
  }

  updateKpiInfo(data: Study[], isInitialLoad = false): void {
    this.isKpiLoading = true;
    this.kpiRequest$.next();

    const clientNamesToUse = this.getClientNamesForCurrentScope();
    let filteredStudies = data;
    let studySkList: string[] = [];

    if (this.sponsorSelected !== 'All') {
      filteredStudies = data.filter((study: Study) => study.clientName === this.sponsorSelected);
    }

    if (isInitialLoad) {
      studySkList = ['all'];
    } else if (this.totalFiltersApplied === 0 && this.sponsorSelected === 'All') {
      studySkList = ['all'];
    } else if (filteredStudies.length > 0) {
      studySkList = filteredStudies.map((study: Study) => study.studySk);
    } else {
      studySkList = [];
    }

    const kpiRequest$ = this.studyService.getPortfolioKpi(studySkList, clientNamesToUse).pipe(
      catchError((err: any) => {
        console.error('Error fetching KPIs:', err);
        return of(null);
      })
    );

    kpiRequest$
      .pipe(takeUntil(this.kpiRequest$), takeUntil(this.destroy$))
      .subscribe((kpiResponse) => {
        if (kpiResponse) {
          this.kpis = {};
          const kpisByKey = kpiResponse.kpis;

          Object.entries(kpisByKey).forEach(([key, kpi]: [string, Kpi]) => {
            this.kpis[key as KpiKeys] = kpi;
            kpi.trendText = this.trendText;
          });
        } else {
          this.kpis = { ...EMPTY_PORTFOLIO_KPIS };
        }

        this.refreshSelectedPortfolioKpi();
        this.isKpiLoading = false;
      });
  }

  private getClientNamesForCurrentScope(): string[] {
    if (this.sponsorSelected !== 'All') {
      return [this.sponsorSelected];
    }

    return this.parentClientNames;
  }

  private loadMilestoneStatusSummaryOnce(): void {
    this.isMilestoneSummaryLoading = true;

    this.studyService
      .getPortfolioMilestones(this.parentClientNames)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: any) => {
          console.error('Error fetching portfolio milestones:', err);
          return of([]);
        })
      )
      .subscribe((milestones) => {
        this.portfolioMilestones = milestones;
        this.milestoneFilterService.initializeFilters(this.portfolioMilestones);
        this.isMilestoneSummaryLoading = false;
      });
  }

  private subscribeToMilestonePanelFilterState(): void {
    this.milestoneFilterService.filterState$
      .pipe(auditTime(16), takeUntil(this.destroy$))
      .subscribe((filterState) => {
        this.updateKpiPanelFilterButtonState(filterState);
        this.handleMilestonePanelFiltersChange(filterState.filteredData);
      });
  }

  private handleMilestonePanelFiltersChange(filteredMilestones: PortfolioMilestone[]): void {
    this.allMilestoneStatusSummaryData = this.mapMilestonesToSummaryData(filteredMilestones);
    this.applyChartSourceFiltersToSummary();

    const filteredStudySkList = this.getFilteredStudySkListFromMilestones(filteredMilestones);
    this.kpiPanelFilteredStudyCount = filteredStudySkList.length;
    this.refreshKpiPanelTimeCycleData(filteredStudySkList);
    this.updateKpiPanelFilteredKpiForCurrentFilters(filteredStudySkList);
  }

  private mapMilestonesToSummaryData(
    milestones: PortfolioMilestone[]
  ): MilestoneStatusSummaryItem[] {
    if (!milestones?.length) {
      return this.getEmptyMilestoneStatusSummaryData();
    }

    const counts = new Map<string, number>(
      this.milestoneStatusOrder.map((status) => [status, 0] as const)
    );

    milestones.forEach((milestone) => {
      const statusKey = this.normalizeMilestoneStatusKey(milestone.status);
      const canonicalStatus = statusKey ? this.milestoneStatusLookup.get(statusKey) : undefined;
      if (!canonicalStatus) {
        return;
      }
      counts.set(canonicalStatus, (counts.get(canonicalStatus) ?? 0) + 1);
    });

    return this.milestoneStatusOrder.map((label) => ({
      label,
      value: counts.get(label) ?? 0
    }));
  }

  private normalizeMilestoneStatusKey(status?: string): string | undefined {
    if (!status) return undefined;
    return status
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/\s*\|\s*/g, ' | ')
      .toLowerCase();
  }

  private getEmptyMilestoneStatusSummaryData(): MilestoneStatusSummaryItem[] {
    return this.milestoneStatusOrder.map((label) => ({ label, value: 0 }));
  }

  private getEmptyPortfolioTimeCycleChartData(): KpiTimeCycleChartData {
    return {
      identifier: 'timeCycleChart',
      title: 'Average cycle time',
      tooltipText:
        'Cycle time is the elapsed time from the first milestone’s start to the second milestone’s completion.',
      labels: [],
      offset: [],
      timeWeeks: [],
      category: [],
      cycleCount: 0
    };
  }

  private mapPortfolioTimeCycleChartData(timeCycleResponse: TimeCycleChart): KpiTimeCycleChartData {
    return {
      ...this.getEmptyPortfolioTimeCycleChartData(),
      labels: timeCycleResponse.labels ?? [],
      offset: timeCycleResponse.offset ?? [],
      timeWeeks: timeCycleResponse.timeWeeks ?? [],
      category: timeCycleResponse.category ?? [],
      cycleCount: timeCycleResponse.cycleCount ?? 0
    };
  }

  private getStudySkListForActiveKpiPanelScope(): string[] {
    if (this.portfolioMilestones.length > 0) {
      return this.getFilteredStudySkListFromMilestones(
        this.milestoneFilterService.getCurrentState().filteredData
      );
    }

    return this.displayedData.map((study) => String(study.studySk));
  }

  private refreshKpiPanelTimeCycleData(studySkList?: string[]): void {
    if (
      !this.selectedPortfolioKpi ||
      this.selectedPortfolioKpi.key !== this.portfolioMilestonesKpiKey
    ) {
      this.kpiPanelCycleRequest$.next();
      this.portfolioTimeCycleChartData = this.getEmptyPortfolioTimeCycleChartData();
      this.isKpiPanelCycleLoading = false;
      return;
    }

    const scopedStudySkList = studySkList ?? this.getStudySkListForActiveKpiPanelScope();

    if (scopedStudySkList.length === 0) {
      this.kpiPanelCycleRequest$.next();
      this.portfolioTimeCycleChartData = this.getEmptyPortfolioTimeCycleChartData();
      this.isKpiPanelCycleLoading = false;
      return;
    }

    this.isKpiPanelCycleLoading = true;
    this.kpiPanelCycleRequest$.next();

    this.studyService
      .getPortfolioAverageTimeCycle(scopedStudySkList, this.getClientNamesForCurrentScope())
      .pipe(
        takeUntil(this.kpiPanelCycleRequest$),
        takeUntil(this.destroy$),
        catchError((err: any) => {
          console.error('Error fetching panel cycle chart:', err);
          return of(null);
        })
      )
      .subscribe((response) => {
        this.portfolioTimeCycleChartData = response
          ? this.mapPortfolioTimeCycleChartData(response)
          : this.getEmptyPortfolioTimeCycleChartData();
        this.isKpiPanelCycleLoading = false;
      });
  }

  get kpiPanelFiltersApplied(): Record<string, string[]> {
    return this.kpiPanelFiltersAppliedState;
  }

  get totalKpiPanelFiltersApplied(): number {
    return this.totalKpiPanelFiltersAppliedState;
  }

  onKpiSummaryStatusFilterToggle(statusLabel: string): void {
    const canonicalStatus = this.toCanonicalMilestoneStatus(statusLabel);
    if (!canonicalStatus) return;

    if (this.hasPanelStatusFilter(canonicalStatus)) {
      this.milestoneFilterService.removeFilterFromAllSources('status', canonicalStatus, 'chart');
      return;
    }

    this.milestoneFilterService.addFilter('status', canonicalStatus, 'chart');
    this.milestoneFilterService.addFilter('status', canonicalStatus, 'table');
  }

  onKpiPanelRemoveFilter(event: { field: string; value: string }): void {
    const field = event.field === 'milestoneStatus' ? 'status' : event.field;
    if (!field) {
      return;
    }

    const value =
      field === 'status'
        ? (this.toCanonicalMilestoneStatus(event.value) ?? event.value)
        : event.value;
    this.milestoneFilterService.removeFilterFromAllSources(field, value, 'kpi');
    this.milestoneFilterService.removeStudyFilter(
      field === 'status' ? 'milestoneStatus' : field,
      value,
      'kpi'
    );
  }

  onKpiPanelClearAllFilters(): void {
    this.milestoneFilterService.clearAllPanelFilters('kpi');
    this.milestoneFilterService.clearAllStudyFilters('kpi');
  }

  private toCanonicalMilestoneStatus(status?: string): string | undefined {
    const statusKey = this.normalizeMilestoneStatusKey(status);
    if (!statusKey) return undefined;
    return this.milestoneStatusLookup.get(statusKey);
  }

  private applyChartSourceFiltersToSummary(): void {
    const chartStatusFilters = this.getPanelMilestoneStatusFilters();

    if (chartStatusFilters.size === 0) {
      this.milestoneStatusSummaryData = [...this.allMilestoneStatusSummaryData];
      this.kpiPanelFilteredStudyCount = this.displayedData.length;
      this.kpiPanelFilteredKpi = undefined;
      this.isKpiPanelKpiLoading = false;
      this.kpiPanelRequest$.next();
      return;
    }

    this.milestoneStatusSummaryData = this.allMilestoneStatusSummaryData.filter((item) =>
      chartStatusFilters.has(item.label)
    );
  }

  private getPanelMilestoneStatusFilters(): Set<string> {
    const displayFilters = this.milestoneFilterService.getDisplayFilters();
    const statusFilters = displayFilters['status'];

    if (!statusFilters || statusFilters.size === 0) {
      return new Set<string>();
    }

    const canonicalFilters = new Set<string>();
    statusFilters.forEach((status) => {
      const canonicalStatus = this.toCanonicalMilestoneStatus(status);
      if (canonicalStatus) {
        canonicalFilters.add(canonicalStatus);
      }
    });

    return canonicalFilters;
  }

  private hasPanelStatusFilter(statusLabel: string): boolean {
    return this.getPanelMilestoneStatusFilters().has(statusLabel);
  }

  private updateKpiPanelFilterButtonState(filterState: {
    appliedFilters: Record<string, Set<string>>;
    appliedStudyFilters: Record<string, Set<string>>;
  }): void {
    const combinedFilters: Record<string, Set<string>> = {};
    const appendFilters = (filters: Record<string, Set<string>>, source: 'panel' | 'study') => {
      Object.entries(filters ?? {}).forEach(([rawField, values]) => {
        const field = source === 'study' && rawField === 'milestoneStatus' ? 'status' : rawField;
        if (!combinedFilters[field]) {
          combinedFilters[field] = new Set<string>();
        }
        values.forEach((value) => combinedFilters[field].add(value));
      });
    };

    appendFilters(filterState.appliedFilters, 'panel');
    appendFilters(filterState.appliedStudyFilters, 'study');

    this.kpiPanelFiltersAppliedState = Object.entries(combinedFilters).reduce(
      (acc, [field, values]) => {
        const appliedValues = Array.from(values);
        if (appliedValues.length > 0) {
          acc[field] = appliedValues;
        }
        return acc;
      },
      {} as Record<string, string[]>
    );

    this.totalKpiPanelFiltersAppliedState = Object.values(this.kpiPanelFiltersAppliedState).reduce(
      (count, values) => count + values.length,
      0
    );
  }

  private getFilteredStudySkListFromMilestones(filteredMilestones: PortfolioMilestone[]): string[] {
    return Array.from(new Set(filteredMilestones.map((milestone) => String(milestone.studySk))));
  }

  private updateKpiPanelFilteredKpiForCurrentFilters(studySkList: string[]): void {
    if (!this.milestoneFilterService.hasActivePanelFilters()) {
      this.kpiPanelFilteredKpi = undefined;
      this.isKpiPanelKpiLoading = false;
      this.kpiPanelRequest$.next();
      return;
    }

    if (!this.selectedPortfolioKpi) {
      return;
    }

    this.refreshKpiPanelFilteredKpi(studySkList);
  }

  private refreshKpiPanelFilteredKpi(studySkList: string[]): void {
    if (!this.selectedPortfolioKpi || !this.milestoneFilterService.hasActivePanelFilters()) {
      this.kpiPanelFilteredKpi = undefined;
      this.isKpiPanelKpiLoading = false;
      this.kpiPanelRequest$.next();
      return;
    }

    this.isKpiPanelKpiLoading = true;
    this.kpiPanelRequest$.next();

    this.studyService
      .getPortfolioKpi(studySkList, this.getClientNamesForCurrentScope())
      .pipe(
        takeUntil(this.kpiPanelRequest$),
        takeUntil(this.destroy$),
        catchError((err: any) => {
          console.error('Error fetching panel KPI:', err);
          return of(null);
        })
      )
      .subscribe((response) => {
        const filteredKpi = response?.kpis?.[this.portfolioMilestonesKpiKey];
        if (filteredKpi) {
          filteredKpi.trendText = this.trendText;
          this.kpiPanelFilteredKpi = filteredKpi;
        } else {
          this.kpiPanelFilteredKpi = undefined;
        }
        this.isKpiPanelKpiLoading = false;
      });
  }

  onPageChange(event: CustomEvent) {
    const nextPage = event.detail.activePage;
    if (nextPage !== this.currentPage) {
      this.currentPage = nextPage;
      this.applySortAndPagination();
    }
  }

  private applySortAndPagination(): void {
    let displayData = [...this.displayedData];

    if (this.currentSort.active && this.currentSort.direction) {
      const column = this.currentSort.active as keyof Study;
      const ascending = this.currentSort.direction === 'asc';

      this.currentSortColumn = column as string;
      this.currentSortDirection = this.currentSort.direction;
      const sortOrderEntry = this.getSortOrderEntry(column as string);

      if (sortOrderEntry?.alphabetical) {
        displayData = sortStandard(displayData, column, ascending);
      } else {
        const customOrder = sortOrderEntry
          ? ascending
            ? sortOrderEntry.sortingAscending
            : sortOrderEntry.sortingDescending
          : [];

        if (customOrder && customOrder.length > 0) {
          displayData = sortByCustomOrder(displayData, column, customOrder);
        } else {
          displayData = sortStandard(displayData, column, ascending);
        }
      }
    }

    const startIndex = (this.currentPage - 1) * PAGINATION_SIZE;
    const endIndex = Math.min(startIndex + PAGINATION_SIZE, displayData.length);
    this.totalResults = displayData.length;
    this.tableData.data = displayData.slice(startIndex, endIndex);
  }

  private getSortOrderEntry(columnType: string): SortOrder | undefined {
    return SORT_ORDER.find((order) => order.type.toLowerCase() === columnType.toLowerCase());
  }

  contextmenuFocusOut() {
    this.showHeaderMenu = false;
  }

  onMenuSort(direction: 'asc' | 'desc', column: string) {
    this.activeMenuColumn = null;
    if (this.currentSortColumn === column && this.currentSortDirection === direction) {
      //Reset to default sort if sorting direction is selected twice on the same column
      this.currentSort = { active: 'protocolNumber', direction: 'asc' };
      this.currentSortColumn = 'protocolNumber';
      this.currentSortDirection = 'asc';
    } else {
      this.currentSort = { active: column as keyof Study, direction };
      this.currentSortColumn = column;
      this.currentSortDirection = direction;
    }
    this.applySortAndPagination();
    this.showHeaderMenu = false;
  }

  toggleMenu(event: MouseEvent, columnId: string) {
    event.stopPropagation();
    if (this.activeMenuColumn === columnId) {
      this.activeMenuColumn = null;
    } else {
      this.activeMenuColumn = columnId;
    }
  }

  openExpandedView() {
    this.dialog.open(ExpandedViewComponent, {
      data: {
        viewTitle: this.expandedChartTitle,
        viewDescription: ' ',
        chartOption: EXPANDED_BAR_CHART_OPTION,
        initOption: INIT_CHART_OPTION_BIG,
        sortedData$: this.studyFilterService.filterState$.pipe(
          map((filterState: FilterState) => {
            const chartData = parseToLineChartData(
              'therapeuticArea',
              filterState.filteredData,
              ({ therapeuticArea }) => therapeuticArea
            );
            return chartData;
          })
        ),
        showAllKeys: true,
        field: 'therapeuticArea',
        filterService: this.studyFilterService,
        chartType: 'default'
      },
      panelClass: 'fullscreen-dialog',
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw'
    });
  }

  onFeedbackModalClosed(modalSelection: 'yes' | 'no' | 'cancel'): void {
    this.showModal = false;
    if (modalSelection === 'yes') {
      this.showProviderModal = true;
    }
  }

  onFeedbackOpenRequested(payload: MailtoPayload): void {
    this.emailPayload = {
      to: (payload.to ?? '').trim(),
      subject: payload.subject ?? '',
      body: payload.body ?? ''
    };
    this.showModal = true;
  }

  onTermClicked(metadata: Record<string, any>): void {
    const id = metadata?.['id'];
    if (this.isStudy(metadata) && id !== null && id !== undefined) {
      this.router.navigate(['/portfolio/study-details', id]);
    }
  }

  isStudy(metadata: Record<string, any>): boolean {
    return metadata['type'] == 'study';
  }

  onProviderSelected(provider: EmailProvider | 'cancel'): void {
    this.showProviderModal = false;
    if (!this.emailPayload || provider === 'cancel') return;

    const { to, subject, body } = this.emailPayload;
    const su = encodeURIComponent(subject);
    const bo = encodeURIComponent(body);
    const toParam = encodeURIComponent(to);

    let url = '';

    switch (provider) {
      case 'gmail':
        url = `https://mail.google.com/mail/u/0/?fs=1&tf=cm&to=${toParam}&su=${su}&body=${bo}`;
        break;
      case 'outlook':
        url = `https://outlook.office.com/mail/deeplink/compose?to=${toParam}&subject=${su}&body=${bo}`;
        break;
      case 'default':
        url = `mailto:${to}?subject=${su}&body=${bo}`;
        break;
    }

    window.open(url, '_blank', 'noopener');
  }

  getKpiKey(stringKey: string): KpiKeys {
    return stringKey as KpiKeys;
  }

  private remountPaginatorAndSet(): void {
    this.showPaginator = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.showPaginator = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        const el = this.paginatorRef?.nativeElement as any;
        if (!el) return;
        el.activepage = this.currentPage;
        (el as HTMLElement).setAttribute('activepage', String(this.currentPage));
        this.cdr.detectChanges();
      }, 0);
    }, 0);
  }

  private resetPaginatorUI(): void {
    this.currentPage = 1;
    this.remountPaginatorAndSet();
  }

  onProtocolMouseEnter(element: Study): void {
    this.isProtocolAnchorHovered = true;

    if (this.protocolTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    this.protocolTooltipElement = element;
    this.protocolTooltipOpen = true;
  }

  onProtocolMouseLeave(): void {
    this.isProtocolAnchorHovered = false;
    this.protocolTooltipHide();
  }

  onPortfolioSidePanelExpand(): void {
    if (!this.selectedPortfolioKpi) {
      return;
    }
    this.portfolioExpandedPanelExpanded = true;
  }

  onPortfolioExpandedCollapse(): void {
    this.portfolioExpandedPanelExpanded = false;
    this.milestoneFilterService.restoreOriginalStudyFilters();
  }

  onPortfolioExpandedClose(): void {
    this.portfolioExpandedPanelExpanded = false;
    this.selectedKpiService.setSelectedKpi(undefined);
  }

  onProtocolTooltipHover(): void {
    this.isProtocolTooltipHovered = true;

    if (this.protocolTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }
  }

  onProtocolTooltipLeave(): void {
    this.isProtocolTooltipHovered = false;
    this.protocolTooltipHide();
  }

  private protocolTooltipHide(): void {
    if (this.protocolTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    if (this.isProtocolAnchorHovered || this.isProtocolTooltipHovered) {
      return;
    }

    this.protocolTooltipHideTimeoutId = window.setTimeout(() => {
      this.protocolTooltipOpen = false;
      this.protocolTooltipHideTimeoutId = null;
    }, StudyPortfolioComponent.HIDE_TOOLTIP_DELAY_MS);
  }

  onMilestoneMouseEnter(element: Study): void {
    if (!this.shouldShowMilestoneTooltip(element)) {
      return;
    }

    this.isMilestoneAnchorHovered = true;

    if (this.milestoneTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.milestoneTooltipHideTimeoutId);
      this.milestoneTooltipHideTimeoutId = null;
    }

    this.milestoneTooltipElement = element;
    this.milestoneTooltipOpen = true;
  }

  onMilestoneMouseLeave(): void {
    this.isMilestoneAnchorHovered = false;
    this.milestoneTooltipHide();
  }

  onMilestoneTooltipHover(): void {
    this.isMilestoneTooltipHovered = true;

    if (this.milestoneTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.milestoneTooltipHideTimeoutId);
      this.milestoneTooltipHideTimeoutId = null;
    }
  }

  onMilestoneTooltipLeave(): void {
    this.isMilestoneTooltipHovered = false;
    this.milestoneTooltipHide();
  }

  getMilestoneTooltipDate(value: string | null | undefined): string {
    return this.commonFunctionsService.formatDate(value ?? null);
  }

  private milestoneTooltipHide(): void {
    if (this.milestoneTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.milestoneTooltipHideTimeoutId);
      this.milestoneTooltipHideTimeoutId = null;
    }

    if (this.isMilestoneAnchorHovered || this.isMilestoneTooltipHovered) {
      return;
    }

    this.milestoneTooltipHideTimeoutId = window.setTimeout(() => {
      this.milestoneTooltipOpen = false;
      this.milestoneTooltipHideTimeoutId = null;
    }, StudyPortfolioComponent.HIDE_TOOLTIP_DELAY_MS);
  }

  private mapResponseState(
    insights: { status: 'success'; data: Message[] } | { status: 'error'; errorCode: number }
  ) {
    if (insights.status === 'success') {
      return insights.data[1]?.content ?? 'No portfolio insights available at this time.';
    }

    throw new Error(`Failed to load portfolio insights (error code: ${insights.errorCode})`);
  }

  get selectedPortfolioKpiForPanel(): { key: KpiKeys; kpi: Kpi } | undefined {
    if (!this.selectedPortfolioKpi) {
      return undefined;
    }

    if (!this.kpiPanelFilteredKpi) {
      return this.selectedPortfolioKpi;
    }

    return {
      key: this.selectedPortfolioKpi.key,
      kpi: this.kpiPanelFilteredKpi
    };
  }

  get portfolioSidePanelHeaderTitle(): string {
    return this.selectedPortfolioKpiForPanel?.kpi.name ?? '--';
  }

  get portfolioKpiSecondaryValue(): string {
    const selectedKpi = this.selectedPortfolioKpiForPanel;
    if (!selectedKpi) return '--';
    return (
      this.kpiCommonFunctionsService.getKpiDisplaySecondaryValue(
        selectedKpi.kpi,
        selectedKpi.key
      ) ?? '--'
    );
  }

  get portfolioKpiDisplayValue(): string {
    const selectedKpi = this.selectedPortfolioKpiForPanel;
    if (!selectedKpi) return '--';
    const value = this.kpiCommonFunctionsService.getKpiDisplayValue(
      selectedKpi.kpi,
      selectedKpi.key
    );
    const unit = this.kpiCommonFunctionsService.getKpiDisplayUnit(selectedKpi.kpi.type);
    return value !== undefined ? `${value}${unit}` : '--';
  }

  get portfolioExpandedStudiesText(): string {
    if (this.milestoneFilterService.hasActivePanelFilters()) {
      return `${this.kpiPanelFilteredStudyCount} of ${this.displayedData.length} studies`;
    }

    return `${this.displayedData.length} of ${this.currentStudiesDisplayed} studies`;
  }

  private handlePortfolioKpiSelection(kpiKey: KpiKeys | undefined): void {
    if (kpiKey !== this.portfolioMilestonesKpiKey) {
      this.closePortfolioSidePanel();
      return;
    }

    const selectedKpi = this.kpis[kpiKey];
    if (!selectedKpi) {
      this.closePortfolioSidePanel();
      return;
    }

    this.selectedPortfolioKpi = { key: kpiKey, kpi: selectedKpi };
    this.portfolioSidePanelExpanded = true;
    this.refreshKpiPanelTimeCycleData();
  }

  private refreshSelectedPortfolioKpi(): void {
    if (!this.selectedPortfolioKpi) {
      return;
    }

    const updatedKpi = this.kpis[this.selectedPortfolioKpi.key];
    if (!updatedKpi) {
      this.closePortfolioSidePanel();
      return;
    }

    this.selectedPortfolioKpi = { key: this.selectedPortfolioKpi.key, kpi: updatedKpi };

    if (this.milestoneFilterService.hasActivePanelFilters()) {
      const filteredStudySkList = this.getFilteredStudySkListFromMilestones(
        this.milestoneFilterService.getCurrentState().filteredData
      );
      this.kpiPanelFilteredStudyCount = filteredStudySkList.length;
      this.refreshKpiPanelFilteredKpi(filteredStudySkList);
      this.refreshKpiPanelTimeCycleData(filteredStudySkList);
      return;
    }

    this.refreshKpiPanelTimeCycleData();
  }

  private closePortfolioSidePanel(): void {
    this.kpiPanelRequest$.next();
    this.kpiPanelCycleRequest$.next();
    this.kpiPanelFilteredKpi = undefined;
    this.isKpiPanelKpiLoading = false;
    this.milestoneFilterService.clearAllPanelFilters('kpi');
    this.milestoneFilterService.restoreOriginalStudyFilters();
    this.isKpiPanelCycleLoading = false;
    this.portfolioTimeCycleChartData = this.getEmptyPortfolioTimeCycleChartData();
    this.selectedPortfolioKpi = undefined;
    this.portfolioSidePanelExpanded = false;
    this.portfolioExpandedPanelExpanded = false;
  }
}
